import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import LinehaulOutboundPlanningService from "app/data/linehaul-outbound-planning/linehaulOutboundPlanningService";
import { PlanningFreightCategory } from "app/data/linehaul-outbound-planning/models";
import { PLANNING_LIST_QUERY_OPTIONS } from "./planningListQueryOptions";
import { enrichPlanningFreightWithPriorityCustomers } from "../utils/enrichPlanningFreightWithPriorityCustomers";
import { normalizeAppointmentsOnFreightList } from "../utils/normalizeAppointmentOnFreight";

function normalizeDestinationTerminalNumbers(
  destinationTerminalNumbers: number[] | undefined
): number[] {
  if (!destinationTerminalNumbers?.length) return [];
  return [...new Set(destinationTerminalNumbers)].sort((a, b) => a - b);
}

export function usePlanningFreightList(
  category: PlanningFreightCategory,
  originTerminalNumber: number | undefined,
  destinationTerminalNumbers: number[] | undefined,
  fetchId: string | undefined,
  enabled: boolean
) {
  const terminals = normalizeDestinationTerminalNumbers(destinationTerminalNumbers);

  return useQuery({
    queryKey: [
      QUERY_KEYS.OUTBOUND_PLANNING_FREIGHT_LIST,
      category,
      originTerminalNumber,
      terminals,
      fetchId,
    ],
    queryFn: async () => {
      const service = LinehaulOutboundPlanningService.getInstance();
      const request = {
        originTerminalNumber: originTerminalNumber!,
        destinationTerminalNumbers: terminals,
      };
      const response =
        category === "inHouse"
          ? await service.getInHouseFreightList(request)
          : await service.getInboundFreightList(request);
      const rows = Array.isArray(response.data) ? response.data : [];
      // Flat Nest/Spring appointment fields → nested appointment for Appt column
      const withAppointments = normalizeAppointmentsOnFreightList(rows);
      // AS400 list → then MySQL priority via portal POST /accounts/priority-customers
      return enrichPlanningFreightWithPriorityCustomers(withAppointments);
    },
    enabled:
      enabled &&
      !!fetchId &&
      typeof originTerminalNumber === "number" &&
      terminals.length > 0,
    ...PLANNING_LIST_QUERY_OPTIONS,
  });
}
